/**
 * Screen-scoped design tokens for **Contract Search** (`/contracts/search`),
 * taken from `Contract Search Standalone.html`.
 *
 * Every orange in the mock (#E1500F) reads the brand red through
 * `signer.orange.*` (CLAUDE.md §11.5); the purple-tinted neutrals and card
 * chrome come from `signer.text` / `contentCard`, which already carry the
 * mock's values (ink, subtitle grey, card border). Every colour here is a theme token.
 */

import { alpha } from "@mui/material/styles";
import { contentCard, focusRing, signer, white } from "theme/colors";

const ink = signer.ink;
const brand = signer.orange.main;

export const contractSearchTokens = {
  layout: {
    threadMaxWidth: 760,
    columnGap: 3,
    /** Space kept under the last turn so it clears the composer band. */
    composerClearance: 2,
  },

  /**
   * Same page frame as Contacts / Sign Requests: the column fills exactly the
   * viewport left under the sticky TopNav and the authenticated layout's own
   * margins, so the card can scroll inside it (values mirror
   * `Contacts.tokens.page` -- keep in step if either changes).
   */
  page: {
    paddingTop: "32px",
    paddingX: "40px",
    /** Breathing room under the composer now that the frame reaches the viewport edge. */
    paddingBottom: "20px",
    topNavHeight: 64,
    topNavBorder: 1,
    layoutMarginTop: { md: 40, lg: 48 },
    layoutPaddingBottom: 48,
  },

  /** Title block + header action, same scale as the Contacts header. */
  header: {
    gap: "12px",
    marginBottom: "22px",
    titleColor: ink,
    titleFontSize: "26px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.02em",
    subtitleColor: signer.text.subtitle,
    subtitleFontSize: "13.5px",
    subtitleMarginTop: "6px",
    /** The bolded contract count inside the subtitle. */
    countColor: signer.text.meta,
    countFontWeight: 700,
    phoneSubtitleFontSize: "13px",
  },

  /** "New search" -- the app's outlined secondary header button (as on Contacts). */
  secondaryButton: {
    height: 44,
    paddingX: "18px",
    bg: white,
    color: signer.orange.main,
    border: signer.orange.main,
    hoverBg: signer.warn.tint,
    radius: "10px",
    fontSize: "13.5px",
    fontWeight: 700,
    gap: "9px",
    iconSize: 16,
  },

  /** "N contracts indexed" pill beside "New search": a quiet neutral, same height as the button. */
  indexedChip: {
    height: 44,
    paddingX: "14px",
    bg: signer.surface.muted,
    color: signer.text.secondary,
    radius: "10px",
    fontSize: "13px",
    fontWeight: 700,
    gap: "8px",
    iconSize: 15,
    iconColor: signer.text.meta,
  },

  /** Spacing of the chat area under the header -- it sits on the page background, no card. */
  chatArea: {
    paddingTop: { xs: "12px", sm: "20px", md: "26px" },
    // Generous gap above the box so it reads as its own band below the content.
    composerPaddingTop: { xs: "20px", sm: "32px", md: "48px" },
    composerPaddingBottom: "4px",
  },

  /** The welcome block before the first question (mock: 54px tile, 19px title, 13.5px body). */
  empty: {
    paddingTop: { xs: "24px", sm: "48px", md: "56px" },
    paddingBottom: "20px",
    iconTile: 54,
    iconTileRadius: "16px",
    iconTileBg: signer.orange.tint,
    iconColor: brand,
    iconSize: 24,
    iconMarginBottom: "16px",
    titleFontSize: { xs: "17px", sm: "19px" },
    titleMarginBottom: "6px",
    bodyColor: signer.text.subtitle,
    bodyFontSize: "13.5px",
    bodyLineHeight: 1.65,
    bodyMaxWidth: 420,
    bodyMarginBottom: "22px",
    chipGap: "8px",
    chipsMaxWidth: 560,
  },

  /** Starter / follow-up question chips (mock: 12.8px/600, 9x14 padding, light border). */
  chip: {
    bg: white,
    border: signer.border.main,
    hoverBg: signer.orange.tint,
    hoverBorder: signer.orange.border,
    color: ink,
    fontSize: "12.8px",
    fontWeight: 600,
    radius: "999px",
    paddingY: "9px",
    paddingX: "14px",
    arrowColor: brand,
  },

  bubble: {
    userBg: ink,
    userColor: white,
    userRadius: "16px 16px 4px 16px",
    userFontSize: "13.5px",
    userMaxWidth: "78%",
    aiAvatarSize: 32,
    aiAvatarRadius: "10px",
    aiAvatarBg: signer.orange.tint,
    aiAvatarColor: brand,
    turnGap: 12,
  },

  card: {
    bg: white,
    border: contentCard.border,
    refusalBorder: signer.orange.border,
    radius: "14px",
    padding: "17px 19px",
    bodyColor: signer.text.secondary,
    bodyFontSize: "14px",
    bodyLineHeight: 1.7,
    titleColor: ink,
    titleFontSize: "14px",
    dividerColor: contentCard.border,
    noteColor: signer.text.meta,
    noteFontSize: "12.5px",
    /** Between Markdown paragraphs / above a per-contract heading. */
    blockGap: "6px",
    headingMarginTop: "8px",
  },

  partialBadge: {
    bg: signer.warn.tint,
    border: signer.warn.border,
    color: signer.warn.text,
    fontSize: "11.5px",
  },

  thinking: {
    dotSize: 7,
    dotColor: brand,
    textColor: signer.text.meta,
    textFontSize: "12.5px",
  },

  matched: {
    headingColor: signer.text.meta,
    headingFontSize: "11px",
    headingLetterSpacing: ".1em",
    countColor: ink,
    countFontSize: "12px",
    rowBorder: signer.border.divider,
    rowHoverBg: signer.surface.subtle,
    rowPadding: "12px 18px",
    tileWidth: 30,
    tileHeight: 36,
    tileBg: signer.orange.tint,
    tileBorder: signer.orange.border,
    tileLabelColor: brand,
    tileLabelFontSize: "8.5px",
    fileColor: ink,
    fileFontSize: "13.5px",
    metaColor: signer.text.meta,
    metaFontSize: "11.5px",
    usedTagBg: signer.success.tint,
    usedTagColor: signer.success.main,
    unusedTagBg: signer.surface.muted,
    unusedTagColor: signer.text.meta,
    tagFontSize: "11px",
    chevronColor: signer.text.faint,
    passagesBg: signer.surface.subtle,
    passagesHeadingColor: signer.text.meta,
    passagesHeadingFontSize: "10.5px",
  },

  passage: {
    bg: white,
    border: contentCard.border,
    hoverBorder: signer.orange.border,
    activeBorder: brand,
    activeBg: signer.orange.faintest,
    radius: "10px",
    padding: "11px 13px",
    pageColor: brand,
    pageFontSize: "11.5px",
    linesColor: signer.text.meta,
    openColor: signer.text.faint,
    openFontSize: "11px",
    quoteColor: signer.text.secondary,
    quoteFontSize: "12.8px",
    quoteLineHeight: 1.7,
    quoteRule: contentCard.border,
  },

  actions: {
    fontSize: "12px",
    fontWeight: 700,
    radius: "8px",
    padding: "6px 11px",
    color: signer.text.muted,
    border: signer.border.main,
    bg: white,
    hoverBg: signer.surface.subtle,
  },

  /** Passage modal body: the quote highlighted on a page-like panel (the mock's viewer). */
  viewer: {
    pageBg: signer.surface.subtle,
    pageBorder: contentCard.border,
    pageRadius: "12px",
    pagePadding: "18px",
    quoteBg: signer.orange.faint,
    quoteRule: brand,
    quoteRadius: "0 9px 9px 0",
    quotePadding: "13px 15px",
    textColor: ink,
    textFontSize: "13.5px",
    textLineHeight: 1.8,
  },

  /** The floating question box (mock: 1.5px light border, 16px radius, 40px send square). */
  composer: {
    bg: white,
    border: signer.border.main,
    borderWidth: "1.5px",
    focusBorder: brand,
    radius: "16px",
    padding: "10px 10px 10px 16px",
    gap: "10px",
    fontSize: "14.5px",
    fontWeight: 500,
    lineHeight: 1.6,
    inputPaddingY: "8px",
    maxRows: 4,
    sendSize: 40,
    sendRadius: "12px",
    sendBg: brand,
    sendHoverBg: signer.orange.dark,
    sendDisabledBg: signer.border.main,
    sendDisabledColor: white,
    sendColor: white,
    sendIconSize: 17,
    hintMarginTop: "9px",
    hintPaddingX: "4px",
    hintGap: "12px",
    noteColor: signer.text.faint,
    noteFontSize: "11.5px",
    noteFontWeight: 600,
    noteIconSize: 13,
    keysColor: signer.text.faint,
    keysFontSize: "11.5px",
    keysFontWeight: 600,
    shadow: `0 4px 18px ${alpha(signer.ink, 0.06)}`,
  },

  focusRing,
} as const;

export default contractSearchTokens;
